/**
 * SquarePaymentInfo.tsx
 * 予約コードに紐づくSquare決済情報をコンパクトに表示する
 */
import React, { useEffect, useState } from 'react';
import { Box, Chip, Typography, CircularProgress } from '@mui/material';

interface LineItem {
  name: string;
  quantity: string;
  amount?: number;
  base_price?: number;
  total?: number;
}

interface InvoiceRecord {
  invoice_id: string;
  status: string;
  computed_amount?: number;
  total_completed_amount?: number;
  currency?: string;
  updated_at?: string;
  created_at?: string;
  line_items?: LineItem[];
}

interface SquarePaymentData {
  status: string;
  invoices?: any[];
  orders?: any[];
  payments?: any[];
  refunds?: any[];
  total_count?: number;
  error?: string;
}

// getSysApiBase は constants/calendar.ts の SYS_API_BASE を使用
import { SYS_API_BASE } from '../../../constants/calendar';

function formatYen(amount: number | null | undefined): string {
  if (amount == null) return '¥0';
  return `¥${amount.toLocaleString()}`;
}

function formatDate(dateStr: string): string {
  try {
    const d = new Date(dateStr);
    return `${d.getMonth() + 1}/${d.getDate()}`;
  } catch {
    return dateStr;
  }
}

interface Props {
  contactCode: string | undefined | null;
}

export default function SquarePaymentInfo({ contactCode }: Props) {
  const [data, setData] = useState<SquarePaymentData | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!contactCode) {
      setData(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setData(null);

    const fetchData = async () => {
      try {
        const sysBase = SYS_API_BASE;
        const res = await fetch(`${sysBase}/payment/search_by_contact.php?contact_code=${encodeURIComponent(contactCode)}`);
        const json = await res.json();
        if (!cancelled) {
          setData(json);
          setLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          setData({ status: 'error', error: String(err) });
          setLoading(false);
        }
      }
    };

    fetchData();
    return () => { cancelled = true; };
  }, [contactCode]);

  if (!contactCode) return null;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 0.5 }}>
        <CircularProgress size={14} />
        <Typography variant="caption" color="text.secondary">Square確認中...</Typography>
      </Box>
    );
  }

  if (!data) return null;

  if (data.status === 'error' || (data.status === 'ok' && (!data.invoices || data.invoices.length === 0))) {
    return (
      <Box sx={{ px: 1, py: 0.5 }}>
        <Typography variant="caption" color="text.disabled">Square未登録</Typography>
      </Box>
    );
  }

  const invoices = data.invoices || [];
  const orders = data.orders || [];
  const payments = data.payments || [];
  const refunds = data.refunds || [];
  const paidInvoices = invoices.filter((inv: any) => inv.status === 'PAID');
  const unpaidInvoices = invoices.filter((inv: any) => inv.status !== 'PAID');

  // 全invoiceのline_itemsをordersから収集
  const allLineItems: LineItem[] = [];
  for (const inv of invoices) {
    const order = orders.find((o: any) => o.order_id === inv.order_id);
    if (order?.line_items) {
      for (const li of order.line_items) {
        allLineItems.push(li);
      }
    }
  }

  return (
    <Box sx={{ px: 1, py: 0.5 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.5 }}>
        {paidInvoices.map((inv: any) => {
          const payment = payments.find((p: any) => p.order_id === inv.order_id);
          const payDate = payment?.created_at || inv.updated_at;
          return (
            <Chip
              key={inv.invoice_id}
              label={`決済済 ${formatYen(inv.computed_amount)}${payDate ? ` (${formatDate(payDate)})` : ''}`}
              size="small"
              sx={{
                backgroundColor: '#e8f5e9',
                color: '#2e7d32',
                fontWeight: 600,
                fontSize: '0.7rem',
                height: 22,
              }}
            />
          );
        })}
        {unpaidInvoices.map((inv: any) => (
          <Chip
            key={inv.invoice_id}
            label={`未決済 ${formatYen(inv.computed_amount)}`}
            size="small"
            sx={{
              backgroundColor: '#fff3e0',
              color: '#e65100',
              fontWeight: 600,
              fontSize: '0.7rem',
              height: 22,
            }}
          />
        ))}
        {refunds.length > 0 && refunds.map((ref: any, idx: number) => (
          <Chip
            key={`refund-${idx}`}
            label={`返金 ${formatYen(Math.round((Number(ref.amount) || 0) / 100))}${ref.created_at ? ` (${formatDate(ref.created_at)})` : ''}`}
            size="small"
            sx={{
              backgroundColor: '#ffebee',
              color: '#c62828',
              fontWeight: 600,
              fontSize: '0.7rem',
              height: 22,
            }}
          />
        ))}
        {invoices.length > 1 && (
          <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
            ({invoices.length}件)
          </Typography>
        )}
      </Box>
      {/* Line items (compact) */}
      {allLineItems.length > 0 && (
        <Box sx={{ mt: 0.5 }}>
          {allLineItems.map((item, idx) => (
            <Typography key={idx} variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.4, pl: 0.5 }}>
              {item.name} x{item.quantity} {formatYen(item.total || item.base_price || item.amount)}
            </Typography>
          ))}
        </Box>
      )}
    </Box>
  );
}
